import { useEffect, useId, useRef, useState } from "react";
import { isDashboardWindow } from "@/growth/axosDashboardModel";
import { daysBetween, shiftDay } from "@/growth/growthMath";
import "./ArcDateRangePicker.css";

// Dates are tenant calendar keys, never instants in the browser's timezone.
// UTC is used only as a stable calendar-arithmetic coordinate system.
const asDate = (key) => new Date(key + "T00:00:00.000Z");
const dateLabel = new Intl.DateTimeFormat("en", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
const monthLabel = new Intl.DateTimeFormat("en", { month: "long", year: "numeric", timeZone: "UTC" });
const weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const ordered = (first, second) => first <= second ? { start: first, end: second } : { start: second, end: first };
function shiftMonth(key, amount) {
  const date = asDate(key);
  const target = new Date(date);
  target.setUTCDate(1);
  target.setUTCMonth(target.getUTCMonth() + amount);
  const last = new Date(target);
  last.setUTCMonth(last.getUTCMonth() + 1);
  last.setUTCDate(0);
  target.setUTCDate(Math.min(date.getUTCDate(), last.getUTCDate()));
  return target.toISOString().slice(0, 10);
}

/** A two-click, automatically applied range. `today` is an optional tenant day key. */
export default function ArcDateRangePicker({ value, onChange, today, label }) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(value.start);
  const [anchor, setAnchor] = useState(null);
  const [hover, setHover] = useState(null);
  const [error, setError] = useState("");
  const container = useRef(null);
  const trigger = useRef(null);
  const uid = useId();
  const month = cursor.slice(0, 7);
  const first = month + "-01";
  const gridStart = shiftDay(first, -asDate(first).getUTCDay());
  const shown = anchor ? ordered(anchor, hover || anchor) : value;
  const title = monthLabel.format(asDate(first));

  useEffect(() => {
    if (open) container.current?.querySelector(`[data-date="${cursor}"]`)?.focus();
  }, [open, cursor]);

  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (event) => {
      if (!container.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);

  function close() {
    setOpen(false);
    trigger.current?.focus();
  }
  function toggle() {
    if (open) { close(); return; }
    setCursor(value.start);
    setAnchor(null);
    setHover(null);
    setError("");
    setOpen(true);
  }
  function pick(key) {
    setCursor(key);
    setHover(null);
    setError("");
    if (!anchor) { setAnchor(key); return; }
    const next = ordered(anchor, key);
    if (!isDashboardWindow(next)) {
      setError("Choose a range of up to 120 days, including the start and end.");
      return;
    }
    onChange(next);
    close();
  }
  function onDayKey(event, key) {
    const shifts = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    let next;
    if (event.key in shifts) next = shiftDay(key, shifts[event.key]);
    else if (event.key === "Home") next = shiftDay(key, -asDate(key).getUTCDay());
    else if (event.key === "End") next = shiftDay(key, 6 - asDate(key).getUTCDay());
    else if (event.key === "PageUp" || event.key === "PageDown") next = shiftMonth(key, (event.key === "PageUp" ? -1 : 1) * (event.shiftKey ? 12 : 1));
    else if (event.key === "Enter" || event.key === " ") { event.preventDefault(); pick(key); return; }
    if (next) { event.preventDefault(); setCursor(next); if (anchor) setHover(next); }
  }

  return <div className="arc-date-picker" ref={container} onBlur={(event) => {
    if (open && event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  }} onKeyDown={(event) => {
    if (open && event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close(); }
  }}>
    <button ref={trigger} type="button" className="arc-date-trigger" data-testid="dashboard-dates"
      aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? uid + "-panel" : undefined} onClick={toggle}>
      <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><rect x="3" y="4" width="14" height="13" rx="2" /><path d="M6 2v4m8-4v4M3 8h14" /></svg>
      <span className="arc-date-mode">RANGE</span><span data-testid="dashboard-window">{label || value.start + " – " + value.end}</span>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d={open ? "m3 8 3-3 3 3" : "m3 4 3 3 3-3"} /></svg>
    </button>
    {open && <div id={uid + "-panel"} className="arc-date-panel" role="dialog" aria-label="Shared reporting dates" aria-describedby={uid + "-help"}>
      <div className="arc-date-header">
        <button type="button" className="arc-date-nav" aria-label="Previous month" onClick={() => { setCursor(shiftMonth(cursor, -1)); setHover(null); }}>‹</button>
        <h2 aria-live="polite">{title}</h2>
        <button type="button" className="arc-date-nav" aria-label="Next month" onClick={() => { setCursor(shiftMonth(cursor, 1)); setHover(null); }}>›</button>
      </div>
      <table role="grid" aria-label={title} className="arc-date-grid" onMouseLeave={() => setHover(null)}>
        <thead><tr>{weekdayNames.map((name) => <th key={name} scope="col" aria-label={name}>{name.slice(0, 2)}</th>)}</tr></thead>
        <tbody>{Array.from({ length: 6 }, (_, week) => <tr key={week}>{Array.from({ length: 7 }, (_, column) => {
          const key = shiftDay(gridStart, week * 7 + column);
          if (!key.startsWith(month)) return <td key={key} />;
          const selected = key >= shown.start && key <= shown.end;
          const edge = key === shown.start ? "start" : key === shown.end ? "end" : undefined;
          return <td key={key} aria-selected={selected} data-in-range={selected || undefined} data-edge={edge}>
            <button type="button" className="arc-date-day" data-date={key} aria-label={dateLabel.format(asDate(key))}
              aria-current={key === today ? "date" : undefined} tabIndex={key === cursor ? 0 : -1}
              onClick={() => pick(key)} onKeyDown={(event) => onDayKey(event, key)}
              onMouseEnter={() => { if (anchor) setHover(key); }} onFocus={() => setCursor(key)}>
              <span>{Number(key.slice(-2))}</span>
            </button>
          </td>;
        })}</tr>)}</tbody>
      </table>
      <div className="arc-date-footer"><div>
        <p id={uid + "-help"} aria-live="polite">{anchor ? "Pick an end date" : "Pick a start date, then an end"}</p>
        <small>{anchor ? anchor : daysBetween(value.start, value.end) + 1 + " days selected"} · Up to 120 days</small>
      </div><button type="button" className="arc-date-cancel" aria-label="Cancel date selection" onClick={close}>Cancel</button></div>
      <span className="arc-date-sr-only">Arrow keys move by day or week. Home and End move to week boundaries. Page Up and Page Down change month; hold Shift to change year. Selecting the end applies the range automatically.</span>
      {error && <p className="arc-date-error" role="alert">{error}</p>}
    </div>}
  </div>;
}
